import "./SpreadsheetFilters.scss";
import Button from "core/buttons/button/Button";
import ClearFiltersButton from "core/buttons/clear-filters-button/ClearFiltersButton";
import Icon from "core/icon/Icon";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import InputText from "core/inputs/input-text/InputText";
import { FORM_INFO } from "constants/forms";
import { FormattedMessage, useIntl } from "react-intl";
import { Typography } from "core/typography/Typography";
import { useEffect, useMemo, useState } from "react";
import { useWindowSize } from "utils/customHooks";
import {
  FILTERS_FOR_NAMES,
  MISSION_STATUS_FILTERS,
  TAG_TYPES,
  VISIBLE_COLUMNS_LIST,
} from "constants/constants";
import {
  formatClientOptions,
  formatNames,
  formatNamesByFilters,
  formatUsersOptions,
} from "../CommonInputs";
import PropTypes from "prop-types";
import { MISSION_MANAGER_ROLES } from "access-roles/roles";

const SpreadsheetFilters = ({
  branchList,
  visibleColumns,
  setVisibleColumns,
  setFaesFilter,
  faesFilter,
  selectedDateObj,
  namesByFilters,
  getNamesByFilters,
  setFilters,
  filters,
  searchWithFilters,
  setCurrentPage,
}) => {
  const intl = useIntl();
  const size = useWindowSize();
  const isMobile = useMemo(() => size.width < 1706, [size]);
  const [color, setColor] = useState("gray-200");

  useEffect(() => {
    setColor(faesFilter ? "violet" : "gray-200");
    return () => {
      if (
        Object.getOwnPropertyNames(selectedDateObj).length === 0 &&
        faesFilter
      ) {
        setFaesFilter(false);
      }
    };
  }, [faesFilter]);

  const hasActiveFilters = () => {
    return (
      filters.name?.length > 0 ||
      filters.client_id?.length > 0 ||
      filters.manager_id?.length > 0 ||
      filters.worker_id?.length > 0 ||
      filters.adv_id?.length > 0 ||
      filters.signed_by_id?.length > 0 ||
      filters.charged_by_id?.length > 0 ||
      filters.status?.length > 0
    );
  };

  const clearAllFilters = () => {
    setFilters((prev) => ({
      ...prev,
      name: "",
      client_id: [],
      manager_id: [],
      worker_id: [],
      adv_id: [],
      signed_by_id: [],
      charged_by_id: [],
      status: [],
    }));
  };

  return (
    <div className="section-1">
      <div>
        <Typography
          className="title-3 violet text-uppercase"
          type="h2"
          label={<FormattedMessage id="filters" defaultMessage="Filtres" />}
        />

        <div className="spreadsheet-filters">
          <div className="line-1">
            <div className="cell-padding">
              <InputText
                errors={{}}
                label={{
                  id: isMobile
                    ? "mission_name"
                    : "filter_mission_spreadsheet_title",
                  defaultMessage:
                    "Nom de la mission (mot dans le nom ou le descriptif de la mission)",
                }}
                id="filter_mission_spreadsheet_title"
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                value={filters.name}
              />
            </div>
            <div className="cell-padding">
              <InputSearchTag
                errors={{}}
                getOptions={(search) =>
                  getNamesByFilters({
                    types: [FILTERS_FOR_NAMES.CLIENT],
                    search: search,
                  })
                }
                label={{
                  id: "customers",
                  defaultMessage: "Clients",
                }}
                limitChoice={10}
                minLength={1}
                onDelete={(selected) =>
                  setFilters((prev) => ({
                    ...prev,
                    client_id: prev.client_id.filter(
                      (item) => item.value !== selected
                    ),
                  }))
                }
                options={formatClientOptions(namesByFilters)}
                popup={FORM_INFO.clientFilter.popup}
                selectedOptions={filters.client_id}
                setValue={(val) =>
                  setFilters((prev) => ({ ...prev, client_id: val }))
                }
                type={TAG_TYPES.CLIENTS}
                id="client-filter"
              />
            </div>
          </div>
          <div className="line-2">
            <div className="cell-padding">
              <InputSearchTag
                errors={{}}
                getOptions={(search) =>
                  getNamesByFilters({
                    role: MISSION_MANAGER_ROLES,
                    types: [FILTERS_FOR_NAMES.PERSON],
                    search,
                  })
                }
                label={{
                  id: "assignment_manager",
                  defaultMessage: "Affectation manager",
                }}
                limitChoice={10}
                minLength={1}
                onDelete={(selected) =>
                  setFilters((prev) => ({
                    ...prev,
                    manager_id: prev.manager_id.filter(
                      (item) => item.value !== selected
                    ),
                  }))
                }
                options={formatUsersOptions({
                  users: namesByFilters,
                  type: TAG_TYPES.PEOPLE,
                })}
                popup={FORM_INFO.managerFilter.popup}
                selectedOptions={filters.manager_id}
                setValue={(val) =>
                  setFilters((prev) => ({ ...prev, manager_id: val }))
                }
                type={TAG_TYPES.PEOPLE}
                id="assignment_manager"
              />
            </div>
            <div className="cell-padding">
              <InputSearchTag
                errors={{}}
                getOptions={(search) => {
                  getNamesByFilters({
                    profile: ["cslt", "str"],
                    types: [FILTERS_FOR_NAMES.PERSON],
                    search,
                  });
                }}
                label={{
                  id: "filter_worker",
                  defaultMessage: "Consultants / Sous-traitants",
                }}
                limitChoice={10}
                minLength={1}
                onDelete={(selected) =>
                  setFilters((prev) => ({
                    ...prev,
                    worker_id: prev.worker_id.filter(
                      (item) => item.value !== selected
                    ),
                  }))
                }
                options={formatNamesByFilters(
                  namesByFilters,
                  TAG_TYPES.CSLT_STR
                )}
                popup={FORM_INFO.CSLT_STR.popup}
                selectedOptions={filters.worker_id}
                setValue={(val) =>
                  setFilters((prev) => ({ ...prev, worker_id: val }))
                }
                type={TAG_TYPES.CSLT_STR}
                id="worker-filter"
              />
            </div>
            <div className="cell-padding">
              <InputSearchTag
                label={{
                  id: "status",
                  defaultMessage: "Statuts",
                }}
                popup={FORM_INFO.statusFilter.popup}
                limitChoice={4}
                options={MISSION_STATUS_FILTERS}
                errors={{}}
                selectedOptions={filters.status}
                setValue={(val) =>
                  setFilters((prev) => ({ ...prev, status: val }))
                }
                onDelete={(selected) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: prev.status.filter(
                      (item) => item.value !== selected
                    ),
                  }))
                }
                id="status-filter"
              />
            </div>
            <div className="cell-padding">
              <InputSearchTag
                errors={{}}
                getOptions={(search) =>
                  getNamesByFilters({
                    profile: ["adv", "radv"],
                    types: [FILTERS_FOR_NAMES.PERSON],
                    search,
                  })
                }
                label={{
                  id: "adv_filter",
                  defaultMessage: "ADV",
                }}
                limitChoice={10}
                minLength={1}
                onDelete={(selected) =>
                  setFilters((prev) => ({
                    ...prev,
                    adv_id: prev.adv_id.filter(
                      (item) => item.value !== selected
                    ),
                  }))
                }
                options={formatNamesByFilters(namesByFilters, TAG_TYPES.PEOPLE)}
                popup={FORM_INFO.ADV.popup}
                selectedOptions={filters.adv_id}
                setValue={(val) =>
                  setFilters((prev) => ({ ...prev, adv_id: val }))
                }
                type={TAG_TYPES.PEOPLE}
                id="worker-filter"
              />
            </div>
          </div>
          <div className="line-3">
            <div className="cell-padding">
              <InputSearchTag
                label={{
                  id: "signed_by_filter",
                  defaultMessage: "Affaires signées par",
                }}
                limitChoice={10}
                onDelete={(selected) =>
                  setFilters((prev) => ({
                    ...prev,
                    signed_by_id: prev.signed_by_id.filter(
                      (item) => item.value !== selected
                    ),
                  }))
                }
                options={formatNames(branchList)}
                popup={FORM_INFO.branchFilter.popup}
                selectedOptions={filters.signed_by_id}
                setValue={(val) =>
                  setFilters((prev) => ({ ...prev, signed_by_id: val }))
                }
                type={TAG_TYPES.BRANCHES}
                errors={{}}
                id="signed-by-filter"
              />
            </div>
            <div className="cell-padding">
              <InputSearchTag
                label={{
                  id: "billed_by_filter",
                  defaultMessage: "Affaires facturées par",
                }}
                limitChoice={10}
                onDelete={(selected) =>
                  setFilters((prev) => ({
                    ...prev,
                    charged_by_id: prev.charged_by_id.filter(
                      (item) => item.value !== selected
                    ),
                  }))
                }
                options={formatNames(branchList)}
                popup={FORM_INFO.branchFilter.popup}
                selectedOptions={filters.charged_by_id}
                setValue={(val) =>
                  setFilters((prev) => ({ ...prev, charged_by_id: val }))
                }
                type={TAG_TYPES.BRANCHES}
                errors={{}}
                id="billed-by-filter"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="link-filter ">
        <button
          className={`filter-wrapper button-style ${
            faesFilter ? "selected" : ""
          } `}
          onClick={() => setFaesFilter(!faesFilter)}
        >
          <div className="filter">
            <Icon icon="file" color={color} width={"22"} height={"21"} />
          </div>
          <FormattedMessage
            id="show_faes"
            defaultMessage="Montrer uniquement mes FAES"
          />
        </button>
        <div className="div-buttons">
          <Button
            color="btn-medium gray-700"
            id="button-search"
            onClick={() => {
              searchWithFilters({ page: 1 });
              setCurrentPage(1);
            }}
          >
            <FormattedMessage id="search_button" defaultMessage="Rechercher" />
          </Button>
          <ClearFiltersButton
            onClear={clearAllFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
      </div>
      <div className="section">
        <div className="spreadsheet-filters">
          <Typography
            className="title-3 violet text-uppercase"
            type="h2"
            label={
              <FormattedMessage
                id="visible_column"
                defaultMessage="Colonnes visibles"
              />
            }
          />
          <div className="line-4">
            <div className="cell-padding">
              <InputSearchTag
                label={{
                  id: "visible_column_label",
                  defaultMessage:
                    "Résultats de recherche visibles dans le tableau de saisie",
                }}
                popup={FORM_INFO.statusFilter.popup}
                limitChoice={20}
                options={VISIBLE_COLUMNS_LIST.filter(
                  (elm) => elm.value !== "mission_name"
                ).map((elm) => ({
                  label: intl.formatMessage(elm.label),
                  value: elm.value,
                  index: elm.index,
                }))}
                errors={{}}
                selectedOptions={visibleColumns}
                setValue={(val) => {
                  setVisibleColumns([...val, VISIBLE_COLUMNS_LIST[0]]);
                }}
                onDelete={(selected) => {
                  setVisibleColumns((old) =>
                    old.filter((item) => item.value !== selected)
                  );
                }}
                id="columns-filter"
              />
            </div>
          </div>
          <Typography
            onClick={() => {
              const inputFile = document.getElementById(
                "input-search-tag-columns-filter"
              );
              if (inputFile) inputFile.click();
            }}
            className="link-1 violet "
            type="h2"
            label={
              <>
                +{" "}
                <FormattedMessage
                  id="add_column"
                  defaultMessage="Ajouter une colonne"
                />
              </>
            }
          />
        </div>
      </div>
    </div>
  );
};

SpreadsheetFilters.propTypes = {
  getClients: PropTypes.func,
  clientsList: PropTypes.array,
  getOptions: PropTypes.func,
  personsByProfileList: PropTypes.array,
  branchList: PropTypes.array,
  visibleColumns: PropTypes.array,
  setVisibleColumns: PropTypes.func,
  searchWithFilters: PropTypes.func,
  setFaesFilter: PropTypes.func,
  faesFilter: PropTypes.bool,
  selectedDateObj: PropTypes.object,
  setCurrentPage: PropTypes.func,
  namesByFilters: PropTypes.array,
  setFilters: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    name: PropTypes.string,
    client_id: PropTypes.array,
    manager_id: PropTypes.array,
    worker_id: PropTypes.array,
    adv_id: PropTypes.array,
    signed_by_id: PropTypes.array,
    charged_by_id: PropTypes.array,
    year: PropTypes.number,
    month: PropTypes.number,
    mission_id: PropTypes.number,
    to_bill_only: PropTypes.bool,
    status: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  }).isRequired,
};

export default SpreadsheetFilters;
